/**
 * Shared client-side file parser for every connector import surface.
 *
 * All uploads (Import Wizard, Manual Sync, Onboarding Wizard) go through
 * @retailsync/connector-adapters so CSV, XLSX, XLS and JSON produce the SAME
 * normalised record shape — headers lower-cased, trimmed and de-duplicated.
 *
 * Previously each page hand-rolled `file.text()` + a comma splitter, which is
 * why .xlsx/.xls uploads silently produced zero usable columns.
 */

export interface ParsedDataFile {
  headers: string[]
  rows: Record<string, any>[]
  totalRows: number
  fileName: string
  fileSize: number
  fileType: 'csv' | 'excel' | 'json'
}

/** Extensions we advertise in the UI. */
export const ACCEPTED_FILE_EXTENSIONS = ['.csv', '.xlsx', '.xls', '.xlsm', '.json']

export const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB

export function isSupportedFileName(name: string): boolean {
  const lower = (name || '').toLowerCase()
  return ACCEPTED_FILE_EXTENSIONS.some(ext => lower.endsWith(ext))
}

/** Human label for the format chip in the upload step. */
export function describeFileType(type: ParsedDataFile['fileType']): string {
  if (type === 'excel') return 'Excel (XLSX/XLS)'
  if (type === 'json') return 'JSON'
  return 'CSV'
}

/** First N non-empty values per column — feeds the auto-mapping heuristics. */
export function buildSampleValues(
  rows: Record<string, any>[],
  headers: string[],
  limit = 10,
): Record<string, any[]> {
  const samples: Record<string, any[]> = {}
  for (const header of headers) {
    samples[header] = rows.slice(0, limit).map(row => row[header])
  }
  return samples
}

/**
 * Parse any supported upload into headers + records.
 * Throws with an actionable message when the file has no usable rows.
 */
export async function parseDataFile(file: File): Promise<ParsedDataFile> {
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`File size must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB`)
  }
  if (file.size === 0) {
    throw new Error('File is empty')
  }
  if (!isSupportedFileName(file.name)) {
    throw new Error('Unsupported file type. Use CSV, Excel (.xlsx/.xls) or JSON.')
  }

  // Dynamic import keeps the xlsx parser out of the initial bundle.
  const { parseTabularFile, sniffFileType } = await import('@retailsync/connector-adapters')

  const buffer = await file.arrayBuffer()
  const sniffed = sniffFileType(buffer, file.name)
  if (sniffed === 'unknown') {
    throw new Error(`Could not detect the format of ${file.name}. Use CSV, Excel or JSON.`)
  }

  const rows = await parseTabularFile(buffer, file.name)

  if (rows.length === 0) {
    throw new Error(
      `No data rows found in ${file.name}. Check that the first sheet contains a header row followed by data.`,
    )
  }

  const headers = Object.keys(rows[0] || {})

  return {
    headers,
    rows: rows as Record<string, any>[],
    totalRows: rows.length,
    fileName: file.name,
    fileSize: file.size,
    fileType: sniffed,
  }
}
